import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import {
  AccountType,
  AddOnRateType,
  getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments,
  Order_Read_Rate_Impact,
  Order_Read_Typeahead,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { FeatureFlags } from '~constants/featureFlags';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { applyProjectRateReference } from '~hooks/useSiteRateSchedule/applyProjectRateReference';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { SiteRateScheduleSaveResult } from '~hooks/useSiteRateSchedule/siteRateScheduleSaveResult';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FreightRateDetailsHandler } from '../../Orders/NewOrderForm/FreightRateDetails';
import { RateDetailsDTO } from '../../Orders/NewOrderForm/schema';
import { applySiteRateSaveResult, FreightRateErrorMap } from './applySiteRateSaveResult';
import { convertSiteRateScheduleToDTO } from './convertSiteRateScheduleToDTO';
import FreightRatesManager from './FreightRatesManager';
import { matchesStarredSrsConditions } from './matchesStarredSrsConditions';
import RateChangeImpactDrawer, {
  type ImpactedOrderRow,
  type RateChangeImpactConfirmResult,
} from './RateChangeImpactDrawer';
import {
  buildSiteRateScheduleOperations,
  buildSiteRateSnapshotMap,
} from './siteRatesDirtyCheck';
import {
  SiteRateScheduleDTO,
  SiteRateScheduleOperations,
  SiteRateScheduleUpdateOperation,
} from './SiteRatesForm';

// Selective rate apply: a `toUpdate` op cascades to existing orders when it
// carries either a real SRS PATCH (non-empty sparse `body`) OR any dirty add-on assignment
// (add-on add/change/remove leaves `body` empty but populates the filtered add-on lists on
// `op.rate`). The non-empty-body branch covers BOTH rate-VALUE edits and card-CONDITION
// edits (pickup/dropoff site, material, equipment type) — condition changes surface as the
// `*_id` fields in `deparseUpdateDTO`, so they ride the same path and open the dialog with
// the post-edit match audience the preview endpoint returns. Both branches must
// open the impact dialog, so the trigger and the preview key off this predicate, not the
// body alone.
const updateOpHasAddOnChange = (op: SiteRateScheduleUpdateOperation): boolean =>
  (op.rate.customerAddOns?.length ?? 0) > 0 ||
  (op.rate.vendorAddOns?.length ?? 0) > 0 ||
  (op.rate.driverAddOns?.length ?? 0) > 0;

const updateOpIsCascading = (op: SiteRateScheduleUpdateOperation): boolean =>
  Object.keys(op.body).length > 0 || updateOpHasAddOnChange(op);

interface RateDetails {
  targetRate: RateBasicWithValue | null;
  targetRateType: RateType | null;
  targetRateValue: number | null;
}

interface FreightRatesProps {
  // Company that owns the rate cards (the project form's company). Falls back to the dispatch-as company.
  companyId?: string;
  defaultDriverRateDetails?: RateDetails;
  isEditingOrder?: boolean;
  isCopyAndEdit?: boolean;
  // Gates the SRS-id stripping inside `convertToSiteRateDTO`. When
  // false/undefined, IDs are preserved on copy so the backend patches the
  // existing records (used when SRS come from a project endpoint and must
  // not be duplicated). When true, IDs are stripped so the backend creates
  // new records (used when copying detached from a project, or when copying
  // a project itself). Always combined with `isCopyAndEdit` at the use site.
  stripSrsIds?: boolean;
  initiallyExposeDriverRate?: boolean;
  alwaysShowDriverRate?: boolean;
  onDirtyStatusChange?: () => void;
  ratesSyncStatus?: 'synced' | 'stamped' | null;
  projectId?: string;
  useVendorRateForDriver?: boolean;
  initialSiteRateSchedules?: SiteRateSchedule[];
  bottomContent?: React.ReactNode;
  accountId?: string;
  prevailingWage?: boolean;
  defaultSiteRateScheduleId?: string | null;
  onDefaultSiteRateScheduleChange?: (id: string | null) => void;
  // Source order's starred SRS, used during Copy & Edit to recover the
  // starred reference by matching SRS conditions when the server ids on
  // the loaded list have been stripped (completed orders, order-owned
  // SRS). When omitted, the recovery is skipped and the starred id is
  // cleared if it doesn't match any loaded row.
  sourceStarredSiteRateSchedule?: SiteRateSchedule | null;
  // Forwarded to FreightRatesManager so it can pick the right delete-
  // confirmation copy. 'order' is the default and reuses the
  // unstar dialog; 'project' fetches affected non-completed orders and lists
  // them.
  // Required: every host names its form family; feeds the manager's and
  // the impact drawer's analytics scope.
  scope: 'order' | 'project';
  // Fires whenever it becomes known whether the rate cards are settled. A host
  // that quotes off the cards must wait for `true`: an existing project fetches
  // them, so acting on the empty initial list would quote a card-less project.
  onRateCardsLoadedChange?: (areRateCardsLoaded: boolean) => void;
  // The order's service date (`YYYY-MM-DD`) fuel-surcharge add-on rows
  // resolve their displayed rate at. Billing stamps FSC at the job's start
  // date, so the order form supplies it; the project form omits it (no
  // single service date) and the rows fall back to today's rate.
  orderDate?: string;
  // Selective rate apply. When true, a cascading rate edit opens the impact dialog at submit so the
  // user can choose which existing orders to skip. When false/undefined, saves proceed silently (the
  // BE mass-pins on toggle-off). The two hosts source it INDEPENDENTLY: the project form reads and
  // writes the project's persisted `apply_rate_changes_to_existing_orders`, the order form derives it
  // per-open from the `apply_rate_changes_default_on` company setting plus the user's override and
  // never touches the project's value.
  applyRateChangesToExistingOrders?: boolean;
  // Selective rate apply at the ORDER level. The id of the order this form is editing.
  // Set only by the order form. When present on a project-scoped rate write, the impact dialog omits
  // this order and `origin_order_id` is sent on the PATCH (both toggle states) so the BE applies the
  // edit to this order + the project and leaves the rest alone. Undefined on the project form (no
  // origin), and also on the order form's create / copy & edit surfaces, where the order does not
  // exist yet — `OrderForm` stamps the saved id onto the operations after the POST.
  originOrderId?: string;
  // Selective rate apply at the ORDER level. True when the order form's queued rate ops
  // will write the PROJECT's cards and therefore cascade to other orders. Supplied by the order form,
  // which derives it from the same inputs that pick the write target, so the checkbox's visibility and
  // the cascade handling below can never disagree. Replaces the old `ratesSyncStatus === 'synced' &&
  // originOrderId` test, which missed the create / copy & edit / completed surfaces — all of them write
  // the project's cards, none of them are 'synced' with an id. Undefined on the project form, which has
  // its own scope branch above.
  orderRateWriteIsProjectScoped?: boolean;
  // Selective rate apply at the ORDER level: does the actor hold `edit_project`? Gates the impact
  // sidesheet, which the toggle value alone cannot — the order form forces that value to `true` without
  // the permission, and confirming the sidesheet 403s on the rate-reference endpoint (hard-gated on
  // `edit_project`) and aborts the whole save. Undefined on the project form and the legacy order form,
  // neither of which sets `orderRateWriteIsProjectScoped`, so neither reaches the branch reading this.
  canEditProject?: boolean;
  isFreightRateRequired?: boolean;
}

export type FreightRatesHandler = {
  // Resolves to `true` after `callback` has been invoked with the extracted
  // payload, or `false` when freight validation fails (callback NOT invoked).
  // Await the result and abort submit on `false` — do not wait on the callback
  // alone, which never fires on a validation failure.
  onSubmit?: (
    callback: (data: {
      customerRate: RateDetailsDTO | undefined;
      vendorRate: RateDetailsDTO | undefined;
      driverRate: RateDetailsDTO | undefined;
      customerRateAddOnAssignments: AddOnAssignmentSchema[];
      vendorRateAddOnAssignments: AddOnAssignmentSchema[];
      driverRateAddOnAssignments: AddOnAssignmentSchema[];
      siteRateScheduleOperations: SiteRateScheduleOperations;
    }) => void,
  ) => Promise<boolean>;
  // Applies a partial-save result back into the form's siteRates
  // and snapshot. Returns a summary so the caller (project / order dialog)
  // can decide whether to close the dialog (all-success) or stay open and
  // show inline errors (partial failure).
  applySaveResult?: (result: SiteRateScheduleSaveResult) => {
    hasFailures: boolean;
    succeededCount: number;
    failedCount: number;
  };
  // The cards as they stand in the form right now, unsaved edits included and
  // rows the user removed excluded. Read-only: it runs no validation and
  // writes nothing, so a caller can read it without disturbing the form.
  getSiteRates?: () => SiteRateScheduleDTO[];
};

const FreightRates = forwardRef<FreightRatesHandler, FreightRatesProps>(
  function FreightRates(
    {
      companyId: formCompanyId,
      defaultDriverRateDetails,
      isEditingOrder,
      isCopyAndEdit,
      stripSrsIds,
      initiallyExposeDriverRate,
      alwaysShowDriverRate = false,
      onDirtyStatusChange,
      ratesSyncStatus,
      initialSiteRateSchedules,
      projectId,
      useVendorRateForDriver = false,
      bottomContent,
      accountId,
      prevailingWage,
      defaultSiteRateScheduleId,
      onDefaultSiteRateScheduleChange,
      sourceStarredSiteRateSchedule,
      scope,
      onRateCardsLoadedChange,
      orderDate,
      applyRateChangesToExistingOrders,
      originOrderId,
      orderRateWriteIsProjectScoped,
      canEditProject,
      isFreightRateRequired = false,
    },
    ref,
  ) {
    // Resolve `default_vendor_rate_to_driver` at the parent so
    // `convertToSiteRateDTO` can seed `useVendorRateForDriver` with the final
    // boolean. The dirty-check baseline is built directly from those rows, so
    // capturing the resolved value here prevents the snapshot from later being
    // rebuilt to match the user's edits — which silently dropped SRS updates.
    const {
      isAwaitingTreatment: isAwaitingDefaultVendorRateToDriverFF,
      isReady: isDefaultVendorRateToDriverFFReady,
      isFeatureFlagEnabled: defaultVendorRateToDriverFFEnabled,
    } = useAwaitableFeatureFlag({
      featureFlagKey: FeatureFlags.defaultVendorRateToDriver,
    });

    const customerDetailsRef = useRef<FreightRateDetailsHandler>(null);
    const vendorDetailsRef = useRef<FreightRateDetailsHandler>(null);
    const driverDetailsRef = useRef<FreightRateDetailsHandler>(null);
    const validateFreightRatesRef = useRef<(() => Promise<boolean>) | null>(null);

    const [showDriverRate, setShowDriverRate] = useState(
      initiallyExposeDriverRate || alwaysShowDriverRate || !useVendorRateForDriver,
    );
    // Selective rate apply: impact-dialog gate for project-scope cascading edits (toggle ON).
    const [impactDialogOrders, setImpactDialogOrders] = useState<
      ImpactedOrderRow[] | null
    >(null);
    // Resolver for the in-flight submit awaiting the user's skip choice: the explicit-unchecks +
    // selected date range on confirm, or 'cancel' to abort the save.
    const impactResolveRef = useRef<
      ((result: RateChangeImpactConfirmResult | 'cancel') => void) | null
    >(null);
    // Refetch closure handed to the impact dialog so a range change re-queries the BE for the
    // (server-filtered) in-range orders. Set when the dialog opens; reads the current operations.
    const impactFetchRef = useRef<
      | ((
          from?: string,
          to?: string,
          signal?: AbortSignal,
        ) => Promise<ImpactedOrderRow[]>)
      | null
    >(null);
    // Store current site rates for rendering and operations generation
    const [siteRates, setSiteRates] = useState<SiteRateScheduleDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    // TRE-14509: snapshot of the originally loaded SRS DTOs (keyed by id),
    // captured once after `convertToSiteRateDTO` populates `siteRates` for
    // the first time. Used by `makeOperationsList` to skip re-PATCHing SRSs
    // and add-on assignments the user did not actually edit (which would
    // otherwise clobber concurrent server-side edits made by another user).
    const originalSiteRatesRef = useRef<Map<string, SiteRateScheduleDTO>>(new Map());

    // Expose the driver section when conditions warrant it, but never hide
    // it from here. The toggle relies on CSS to hide the still-mounted
    // FreightRateDetails so the user's typed driver rate survives a
    // toggle-on / toggle-off cycle; unmounting via setShowDriverRate(false)
    // would defeat that.
    useEffect(() => {
      if (defaultDriverRateDetails && !alwaysShowDriverRate) {
        const shouldExpose = Boolean(
          initiallyExposeDriverRate ||
          defaultDriverRateDetails.targetRate ||
          defaultDriverRateDetails.targetRateType ||
          !useVendorRateForDriver,
        );

        if (shouldExpose) setShowDriverRate(true);
      }
    }, [
      defaultDriverRateDetails?.targetRate,
      defaultDriverRateDetails?.targetRateType,
      alwaysShowDriverRate,
      initiallyExposeDriverRate,
      useVendorRateForDriver,
    ]);

    const {
      getProjectSiteRateSchedules,
      getSiteRateScheduleDefaultOnOrders,
      getProjectPreviewOrders,
    } = useSiteRateSchedule();
    // useSiteRateSchedule returns un-memoized fetchers (new identity every render).
    // Reach the preview fetchers through refs so `fetchImpactedOrders` — and, via the
    // useImperativeHandle deps, the `onSubmit` handle it feeds — keeps a stable identity
    // and the handle isn't re-created on every render.
    const getDefaultOnOrdersRef = useRef(getSiteRateScheduleDefaultOnOrders);
    getDefaultOnOrdersRef.current = getSiteRateScheduleDefaultOnOrders;
    // Id-less prospective preview, used only for the first-card create fallback below.
    const getProjectPreviewOrdersRef = useRef(getProjectPreviewOrders);
    getProjectPreviewOrdersRef.current = getProjectPreviewOrders;
    const { toasterStore, userStore } = useStores();
    const { canViewRate } = userStore.getPermissions();

    const dispatchAsCompanyId = getEffectiveUserCompanyId(userStore);
    const companyId = formCompanyId || dispatchAsCompanyId;
    const fetchAddOnAssignments = useCallback(
      async (siteRateId: string) => {
        try {
          // Fetch customer add-ons
          const customerResponse =
            await getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments(
              {
                path: {
                  'company-id': companyId,
                  'site-rate-schedule-id': siteRateId,
                },
                query: { 'filter[account_type]': AccountType.CUSTOMER },
              },
            );

          // Fetch vendor add-ons
          const vendorResponse =
            await getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments(
              {
                path: {
                  'company-id': companyId,
                  'site-rate-schedule-id': siteRateId,
                },
                query: { 'filter[account_type]': AccountType.VENDOR },
              },
            );

          // Fetch driver add-ons. There is no `AccountType.DRIVER` enum value —
          // driver assignments are persisted with `account_type: null`, and the
          // endpoint returns only those null-account-type rows when no
          // `filter[account_type]` is supplied (verified against staging: the
          // unfiltered call returns the driver assignment only, not the
          // customer/vendor ones). `hasDriverAddOn` (TRE-15083) relies on this:
          // `driverAddOns` must contain only genuine driver add-ons.
          const driverResponse =
            await getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments(
              {
                path: {
                  'company-id': companyId,
                  'site-rate-schedule-id': siteRateId,
                },
              },
            );

          // Helper function to convert add-on assignments to schema format
          const convertToAddOnSchema = (addOnAssignment: any) => ({
            isDeleted: false,
            isNew: false,
            addOn: {
              id: addOnAssignment.id,
              name: addOnAssignment.name,
              rateType: addOnAssignment.revenueCostRateType ?? '',
              quantity: addOnAssignment.quantity,
              rate: addOnAssignment.rate,
              percentage: addOnAssignment.percentage,
              externalId: addOnAssignment.externalId,
              accountingId: addOnAssignment.accountingId,
              rateValue:
                addOnAssignment.revenueCostRateType ===
                AddOnRateType.RATE_PERCENT_OF_TOTAL
                  ? addOnAssignment.percentage
                  : addOnAssignment.rate,
              costRateValue: addOnAssignment.costRateValue,
              addOnTypes:
                addOnAssignment.addOnTypes?.map((type: string) => ({
                  id: type,
                  name: type,
                })) || [],
              addOnId: addOnAssignment.addOnId,
              originalAutoAddOnAssignmentId:
                addOnAssignment.originalAutoAddOnAssignmentId,
              fuelSurchargeSchedule: addOnAssignment.addOn?.fuelSurchargeSchedule ?? {
                id: '',
                name: '',
              },
            },
          });

          // Convert responses to schema format
          const customerAddOns = customerResponse.data.data.map((addOnAssignment) =>
            convertToAddOnSchema(AddOnAssignment.parse(addOnAssignment)),
          );

          const vendorAddOns = vendorResponse.data.data.map((addOnAssignment) =>
            convertToAddOnSchema(AddOnAssignment.parse(addOnAssignment)),
          );

          const driverAddOns = driverResponse.data.data.map((addOnAssignment) =>
            convertToAddOnSchema(AddOnAssignment.parse(addOnAssignment)),
          );

          return {
            customerAddOns,
            vendorAddOns,
            driverAddOns,
          };
        } catch (error) {
          console.error('Failed to fetch add-on assignments:', error);
          Sentry.captureException(error, { extra: { companyId, siteRateId } });
          return {
            customerAddOns: [],
            vendorAddOns: [],
            driverAddOns: [],
          };
        }
      },
      [companyId],
    );

    // Convert SiteRateSchedule to SiteRateScheduleDTO for form usage. The
    // per-rate conversion lives in `convertSiteRateScheduleToDTO`; we await
    // each sequentially to preserve the original add-on fetch ordering.
    const convertToSiteRateDTO = useCallback(
      async (siteRates: SiteRateSchedule[]): Promise<SiteRateScheduleDTO[]> => {
        const siteRateDTOs: SiteRateScheduleDTO[] = [];

        for (const rate of siteRates) {
          siteRateDTOs.push(
            await convertSiteRateScheduleToDTO(rate, {
              fetchAddOnAssignments,
              isCopyAndEdit: Boolean(isCopyAndEdit),
              stripSrsIds: Boolean(stripSrsIds),
              defaultVendorRateToDriverFeatureFlagEnabled:
                defaultVendorRateToDriverFFEnabled,
            }),
          );
        }

        return siteRateDTOs;
      },
      [
        fetchAddOnAssignments,
        isCopyAndEdit,
        stripSrsIds,
        defaultVendorRateToDriverFFEnabled,
      ],
    );

    // Generate operations list from current site rates for backend processing
    const makeOperationsList = useCallback(
      (siteRatesList: SiteRateScheduleDTO[]) =>
        buildSiteRateScheduleOperations({
          siteRatesList,
          originalSiteRates: originalSiteRatesRef.current,
          canViewRate,
        }),
      [canViewRate],
    );

    // Fetch site rates once if the projectId is provided,
    // or use pre-fetched initial data (e.g., order-level frozen rates)
    useEffect(() => {
      if (hasInitialized) {
        return;
      }
      // Wait for the FF so `convertToSiteRateDTO` seeds
      // `useVendorRateForDriver` against the real treatment, not the SDK's
      // pre-ready default of `false`.
      if (!isDefaultVendorRateToDriverFFReady) {
        return;
      }

      // Use pre-fetched data when available (completed orders)
      if (initialSiteRateSchedules && initialSiteRateSchedules.length > 0) {
        const convertInitial = async () => {
          setIsLoading(true);
          try {
            const convertedRates = await convertToSiteRateDTO(initialSiteRateSchedules);
            setSiteRates(convertedRates);
            originalSiteRatesRef.current = buildSiteRateSnapshotMap(convertedRates);
            setHasInitialized(true);
          } catch (error) {
            setSiteRates([]);
            setHasInitialized(true);
            try {
              connection.handleRequestError(
                error,
                t('error_messages.site_rate_schedules.failed_to_process'),
              );
            } catch {
              // handleRequestError always throws; cleanup above must run first
            }
          } finally {
            setIsLoading(false);
          }
        };

        convertInitial();
        return;
      }

      // Otherwise fetch from project endpoint
      if (!projectId) {
        return;
      }

      const fetchSiteRates = async () => {
        setIsLoading(true);
        try {
          const rates = await getProjectSiteRateSchedules({ projectId });
          const convertedRates = await convertToSiteRateDTO(rates ?? []);
          setSiteRates(convertedRates);
          originalSiteRatesRef.current = buildSiteRateSnapshotMap(convertedRates);
          setHasInitialized(true);
        } catch (error) {
          setSiteRates([]);
          setHasInitialized(true);
          try {
            connection.handleRequestError(
              error,
              t('error_messages.site.failed_to_fetch'),
            );
          } catch {
            // handleRequestError always throws; cleanup above must run first
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchSiteRates();
    }, [
      projectId,
      hasInitialized,
      convertToSiteRateDTO,
      initialSiteRateSchedules,
      isDefaultVendorRateToDriverFFReady,
    ]);

    // A card-less form has nothing to wait for, so it reports settled straight
    // away; anything with a source to read from settles once that read lands.
    const hasRateCardSource =
      Boolean(projectId) || Boolean(initialSiteRateSchedules?.length);
    const areRateCardsLoaded = hasInitialized || !hasRateCardSource;
    useEffect(() => {
      onRateCardsLoadedChange?.(areRateCardsLoaded);
    }, [areRateCardsLoaded, onRateCardsLoadedChange]);

    // Recover the source order's starred SRS reference on Copy & Edit.
    //
    // 1. If a loaded rate still carries the source SRS server id (the
    //    project-SRS path — rate row id survives the strip), leave the
    //    reference alone and the order PATCH carries it forward.
    // 2. Otherwise (completed orders, order-owned SRS), the loaded rows
    //    have had their server ids stripped because the SRS will be
    //    recreated. Match the source SRS by conditions and re-point the
    //    starred reference at the matching row's clientKey; the order
    //    form resolves clientKey → new server id after SRS save.
    // 3. If nothing matches by id or conditions, drop the reference so
    //    the order POST doesn't carry a stale id the backend would
    //    reject in `validate_default_site_rate_schedule_ownership!`.
    //
    // Runs once per form lifecycle after the SRS list loads — subsequent
    // user-driven changes (star/unstar/delete) must not be reverted by
    // this effect.
    const hasResolvedStarredRef = useRef(false);
    useEffect(() => {
      if (!hasInitialized) return;
      if (!isCopyAndEdit) return;
      if (hasResolvedStarredRef.current) return;
      if (!defaultSiteRateScheduleId) {
        hasResolvedStarredRef.current = true;
        return;
      }
      const idMatch = siteRates.some(
        (rate) => rate.id && !rate.isDeleted && rate.id === defaultSiteRateScheduleId,
      );
      if (idMatch) {
        hasResolvedStarredRef.current = true;
        return;
      }
      if (sourceStarredSiteRateSchedule) {
        const contentMatch = siteRates.find(
          (rate) =>
            !rate.isDeleted &&
            matchesStarredSrsConditions(rate, sourceStarredSiteRateSchedule),
        );
        if (contentMatch?.clientKey) {
          onDefaultSiteRateScheduleChange?.(contentMatch.clientKey);
          hasResolvedStarredRef.current = true;
          return;
        }
      }
      onDefaultSiteRateScheduleChange?.(null);
      hasResolvedStarredRef.current = true;
    }, [
      hasInitialized,
      isCopyAndEdit,
      defaultSiteRateScheduleId,
      siteRates,
      onDefaultSiteRateScheduleChange,
      sourceStarredSiteRateSchedule,
    ]);

    // Per-card error map keyed by clientKey. Cleared by the
    // manager when the user edits a field on the row, and rebuilt
    // each time `applySaveResult` runs after a partial-failure submit.
    const [errorsByClientKey, setErrorsByClientKey] = useState<FreightRateErrorMap>(
      () => new Map(),
    );

    // Bumped when `applySaveResult` mutates `siteRates` from the
    // outside; the manager watches this counter and force-resets its
    // react-hook-form state to the new siteRates so the user sees the
    // settled (post-save) row state on retry.
    const [resetGeneration, setResetGeneration] = useState(0);

    const clearErrorForClientKey = useCallback((clientKey: string) => {
      setErrorsByClientKey((prev) => {
        if (!prev.has(clientKey)) return prev;
        const next = new Map(prev);
        next.delete(clientKey);
        return next;
      });
    }, []);

    // Selective rate apply: the orders impacted by the queued cascading updates, for the impact
    // dialog. Per TRE-15882, the preview endpoint now returns the PROJECT-WIDE
    // cascade-reachable audience regardless of which card the request is keyed to, so a single
    // fetch against ANY one edited card yields the full set — no per-card loop/union. The
    // dialog-trigger predicate (`updateOpIsCascading` / `toDelete`) is unchanged; only the fetch
    // collapses. Fetch failures fail CLOSED (the error propagates so the caller aborts the save)
    // rather than cascading to an undisclosed set of orders.
    const fetchImpactedOrders = useCallback(
      async (
        operations: SiteRateScheduleOperations,
        range?: { from?: string; to?: string; signal?: AbortSignal },
        // TRE-15829: order id to drop from the audience. On an order-origin edit the origin order is
        // cascade-reachable (so the preview returns it), but it is implicitly applied and must never
        // appear as a skippable row — exclude it here so every fetch site (initial, reachability
        // probe, date-range refetch) yields a sibling-only list.
        excludeOrderId?: string,
        // TRE-15848: id of a persisted project card to key the fetch to when the queued ops carry
        // no previewable id of their own (a create-only save). Under v4 the `cascade_apply` audience
        // is PROJECT-WIDE and card-agnostic, so any sibling card id returns the identical set (see
        // the `representativeId` note below). Undefined when the project has no persisted card yet.
        fallbackCardId?: string,
        // TRE-16125 existence-only probe: the "are ANY orders reachable at all?" dialog-open gate
        // only needs `.length > 0`, so fetch a single small page rather than the whole set (see
        // `existenceLimit` on the fetchers). Request TWO rows when excluding the origin: only ONE
        // returned row can equal `excludeOrderId`, so a 2-row page still surfaces a real sibling if
        // one exists (a 1-row page could be just the origin and read as "no siblings"). NEVER use for
        // the referenced-list load — that MUST exhaust (a short list wrongly dereferences orders).
        existenceOnly = false,
      ): Promise<ImpactedOrderRow[]> => {
        if (!companyId) return [];
        const existenceLimit = existenceOnly ? (excludeOrderId ? 2 : 1) : undefined;
        // Preview the cascade-reachable audience for every op that targets an EXISTING card —
        // value edits AND add-on-only edits (toUpdate that `updateOpIsCascading` matches) plus
        // card deletes (toDelete). All are keyed by SRS id. Card creates have no id of their
        // own; their preview keys off `fallbackCardId` (a persisted sibling card) instead,
        // since the endpoint's audience is project-wide.
        const updateIds = operations.toUpdate
          .filter(updateOpIsCascading)
          .map((op) => String(op.rate.id ?? ''));
        const deleteIds = operations.toDelete.map((rate) => String(rate.id ?? ''));
        const editedIds = Array.from(
          new Set([...updateIds, ...deleteIds].filter(Boolean)),
        );

        // The endpoint is keyed by a single SRS id in its path, but under v4 (TRE-15882) the BE
        // returns the same PROJECT-WIDE cascade-reachable audience for ANY card on the project —
        // `SiteRateScheduleService::Orders#call` uses the path id only to resolve the project, then
        // returns the whole `cascade_reachable_for(company).where(project_id:)` set with no per-card
        // filtering. So ONE fetch keyed to any card on the project is the full set: prefer an edited
        // id (value edit / delete), else fall back to a persisted sibling card (a
        // create-only save). Looping the rest would only re-fetch an identical list.
        const representativeId = editedIds[0] ?? fallbackCardId;

        // Resolve the cascade-reachable audience. Both fetches fail CLOSED: a preview-fetch error
        // propagates so the caller aborts the save rather than cascading the change to an undisclosed
        // set of orders (least astonishment). That also keeps the empty-result case trustworthy — an
        // empty list can only mean "genuinely zero impacted orders", never a masked error.
        let orders: Order_Read_Typeahead[] | Order_Read_Rate_Impact[];
        if (representativeId) {
          // Prefer the id-based cascade_apply endpoint keyed to any card on the project (project-wide
          // + card-agnostic under v4). Impact dialog needs the cascade-reachable audience (matches the
          // job); the delete-card warning omits `scope` and keeps the legacy audience. `from`/`to`
          // scope the preview server-side to orders loaded in the range.
          orders = await getDefaultOnOrdersRef.current(companyId, representativeId, {
            scope: 'cascade_apply',
            from: range?.from,
            to: range?.to,
            signal: range?.signal,
            ...(existenceLimit !== undefined ? { existenceLimit } : {}),
          });
        } else if (projectId && operations.toCreate.length > 0) {
          // TRE-15848 first-card fallback: a create on a project with NO persisted card has no id to
          // key the id-based endpoint to. Use the id-less prospective preview (TRE-15836) PROJECT-WIDE
          // (no proposed conditions → create-mode wildcards). This is correct ONLY in this regime: with
          // an empty persisted-card set the BE's winner-flip diff flips every unstarred cascade-reachable
          // order, so the result is exactly the cascade's write audience (read == write). If the project
          // already had a card we would never reach here (representativeId would be set) — wildcards
          // there would return only currently-unmatched orders, NOT the project-wide set.
          orders = await getProjectPreviewOrdersRef.current(companyId, projectId, {
            from: range?.from,
            to: range?.to,
            signal: range?.signal,
            ...(existenceLimit !== undefined ? { existenceLimit } : {}),
          });
        } else {
          // No previewable id and no first-card create to preview (e.g. an edited card with no id, or a
          // create with no projectId in scope on the order form) → nothing to disclose.
          return [];
        }

        const byId = new Map<string, ImpactedOrderRow>();
        for (const o of orders) {
          // The two preview endpoints return DIFFERENT shapes: the `cascade_apply` path yields the
          // richer `Order_Read_Rate_Impact` (all fields below, incl. the `dereferenced`
          // flag from `_read_rate_impact.json.jbuilder`); the first-card prospective-preview fallback
          // yields `Order_Read_Typeahead` (only `id`/`order_id`). Read the richer fields through
          // `Partial<Order_Read_Rate_Impact>` — present on the cascade path, undefined on the typeahead
          // fallback (→ `dereferenced` undefined → the row renders checked, the correct default). `id`/
          // `order_id` are on BOTH branches, so they stay required.
          const row = o as Partial<Order_Read_Rate_Impact> &
            Pick<Order_Read_Rate_Impact, 'id' | 'order_id'>;
          // `row.id` is the ORDER id (matches the reachable ids the BE pins against); drop the
          // origin so it is never listed as a skippable sibling.
          if (excludeOrderId && row.id === excludeOrderId) continue;
          // Dedupe by order id: a single project-wide response is already unique, but the guard is
          // cheap and keeps the row list correct if the paginated endpoint ever repeats an id.
          if (!byId.has(row.id)) {
            byId.set(row.id, {
              id: row.id,
              orderNumber: row.order_id,
              state: row.state,
              loadAt: row.load_at,
              name: row.name,
              dispatchNumberName: row.dispatch_number?.name,
              materialName: row.material?.name,
              pickupSiteName: row.pickup_site_name,
              dropoffSiteName: row.dropoff_site_name,
              dereferenced: row.dereferenced,
            });
          }
        }
        return Array.from(byId.values());
      },
      [companyId, projectId],
    );

    // TRE-16125: reconcile the project's REFERENCED order set as ONE dedicated order-level
    // `rate_reference` call, BEFORE the save's card writes, so deselected AND out-of-range orders are
    // decoupled before any cascade runs. Single referenced-list model: the FE sends the
    // COMPLETE in-range referenced set; the BE dereferences everything reachable NOT in it (in-range
    // unchecked + out-of-range/past) and re-references anything listed that was dereferenced. Fires on
    // EVERY dialog confirm — the forward-looking past-dereference happens with no uncheck present, so
    // there is no delta gate (a plain "apply from today forward" still dereferences past orders). The
    // endpoint is idempotent, so a full retry after a later card-write failure is safe. Returns:
    //   - 'noop'     — the reconcile changed nothing (already in the target state); continue the save.
    //   - 'applied'  — the reconcile changed something; continue + flag it so the orchestrator can tell
    //                  the user which half landed if a later card write fails.
    //   - 'abort'    — the endpoint failed (or the call can't be scoped); ABORT the whole save. Nothing
    //                  else has run yet, and the endpoint is idempotent, so a full retry is safe.
    const runRateReferenceForConfirm = useCallback(
      async (
        result: RateChangeImpactConfirmResult,
        // Order-origin edit (TRE-15829): the editing order's id. Sent as `origin_order_id` so the BE
        // forces it into the referenced set (the origin always keeps its own edit, never dereferenced).
        originOrderIdForReference?: string,
      ): Promise<'noop' | 'applied' | 'abort'> => {
        // No company/project to scope the endpoint would mean applying the cascade WITHOUT reconciling
        // the referenced set — the exact bug this fixes. Fail closed.
        if (!companyId || !projectId) {
          toasterStore.push(
            alert(t('project.form.rate_reference_failed'), AlertTypes.error),
          );
          return 'abort';
        }
        try {
          const outcome = await applyProjectRateReference({
            companyId,
            projectId,
            referencedOrderIds: result.referencedOrderIds,
            confirmFullDereference: result.confirmFullDereference,
            originOrderId: originOrderIdForReference,
          });
          // Transitions-only response: 'applied' iff the reconcile ACTUALLY changed something, so the
          // partial-failure message ("orders now keep their own rates…") shows only when a durable
          // transition really landed — not on the common no-uncheck save where the response is empty.
          return outcome.referencedOrderIds.length > 0 ||
            outcome.dereferencedOrderIds.length > 0
            ? 'applied'
            : 'noop';
        } catch {
          // `applyProjectRateReference` already surfaced the failure (toast + Sentry via
          // handleRequestError) before re-throwing, so we don't toast again here (that would
          // double-toast). Just abort the save — the endpoint is idempotent, so a full retry is safe.
          return 'abort';
        }
      },
      [companyId, projectId, toasterStore],
    );

    useImperativeHandle(
      ref,
      () => ({
        getSiteRates: () => siteRates.filter((rate) => !rate.isDeleted),
        applySaveResult: (result: SiteRateScheduleSaveResult) => {
          const { nextSiteRates, nextSnapshot, errors } = applySiteRateSaveResult(
            siteRates,
            result,
            originalSiteRatesRef.current,
          );
          setSiteRates(nextSiteRates);
          originalSiteRatesRef.current = nextSnapshot;
          setErrorsByClientKey(errors);
          setResetGeneration((n) => n + 1);

          let succeededCount = 0;
          let failedCount = 0;
          for (const c of result.creates) {
            const ok = !c.rateError && c.addOns.every((a) => !a.error);
            ok ? succeededCount++ : failedCount++;
          }
          for (const u of result.updates) {
            const ok = !u.rateError && u.addOns.every((a) => !a.error);
            ok ? succeededCount++ : failedCount++;
          }
          for (const d of result.deletes) {
            d.rateError ? failedCount++ : succeededCount++;
          }

          return {
            hasFailures: failedCount > 0,
            succeededCount,
            failedCount,
          };
        },
        onSubmit: async (callback) => {
          // Returns `true` once `callback` has been invoked with the extracted
          // rate payload, or `false` when freight validation fails (callback is
          // NOT invoked). Callers MUST treat a `false`/never-resolved callback
          // as "abort submit" — a caller that awaits only the callback (e.g. a
          // `new Promise(resolve => onSubmit(resolve))`) would otherwise hang
          // forever on a validation failure.
          if (validateFreightRatesRef.current) {
            const isValid = await validateFreightRatesRef.current();
            if (!isValid) return false;
          } else if (isFreightRateRequired) {
            toasterStore.push(
              alert(
                isAwaitingDefaultVendorRateToDriverFF
                  ? t('form_validation_errors.freight_rates_loading')
                  : t('form_validation_errors.freight_rates_unavailable'),
                AlertTypes.error,
              ),
            );
            return false;
          }

          const [customerRate, vendorRate, driverRate, operations] = await Promise.all([
            new Promise<{
              data: RateDetailsDTO | undefined;
              addOnAssignments: AddOnAssignmentSchema[];
            }>((resolve) => {
              if (customerDetailsRef.current?.onSubmit) {
                customerDetailsRef.current.onSubmit((data, addOnAssignments) => {
                  resolve({ data, addOnAssignments });
                });
              } else {
                resolve({ data: undefined, addOnAssignments: [] });
              }
            }),
            new Promise<{
              data: RateDetailsDTO | undefined;
              addOnAssignments: AddOnAssignmentSchema[];
            }>((resolve) => {
              if (vendorDetailsRef.current?.onSubmit) {
                vendorDetailsRef.current.onSubmit((data, addOnAssignments) => {
                  resolve({ data, addOnAssignments });
                });
              } else {
                resolve({ data: undefined, addOnAssignments: [] });
              }
            }),
            new Promise<{
              data: RateDetailsDTO | undefined;
              addOnAssignments: AddOnAssignmentSchema[];
            }>((resolve) => {
              // The driver section may still be mounted (CSS-hidden) when the
              // toggle is on so we can restore the user's input on toggle-off.
              // Skip its submit in that case — NewOrderForm derives the saved
              // driver values from vendor instead.
              const shouldSubmitDriver =
                showDriverRate && (isEditingOrder || !useVendorRateForDriver);
              if (driverDetailsRef.current?.onSubmit && shouldSubmitDriver) {
                driverDetailsRef.current.onSubmit((data, addOnAssignments) => {
                  resolve({ data, addOnAssignments });
                });
              } else {
                resolve({ data: undefined, addOnAssignments: [] });
              }
            }),
            new Promise<SiteRateScheduleOperations>((resolve) => {
              // Generate operations from current site rates
              const operations = makeOperationsList(siteRates);
              resolve(operations);
            }),
          ]);

          // Selective rate apply: a project-scoped rate change with the toggle ON opens
          // the impact dialog so the user chooses which existing orders to skip. The trigger covers
          // rate-value edits, card-condition edits (via the non-empty body), add-on add/change/remove,
          // card delete, and card CREATE (a create shows the SAME project-wide audience as a
          // value edit). All queued ops fold into ONE merged dialog with a single combined impacted
          // list. Only opens when at least one order is actually impacted (zero-impact →
          // silent save). Order scope / toggle off never reach this block.
          const hasCascadingChange =
            operations.toUpdate.some(updateOpIsCascading) ||
            operations.toDelete.some((rate) => Boolean(rate.id)) ||
            operations.toCreate.length > 0;
          // A create-only save carries no edited/deleted SRS id, so the id-based
          // `cascade_apply` preview has nothing to key on. That endpoint's audience is PROJECT-WIDE and
          // card-agnostic under v4, so key it to any persisted sibling card instead — the first card on
          // the form that still has a server id and isn't being deleted. `undefined` when the project has
          // no persisted card yet (creating the very first card): the preview can't run and the save
          // proceeds silently (documented v1 gap). For value edits / deletes this is ignored — the fetch
          // prefers the edited id.
          const representativeCardId =
            siteRates.find((rate) => Boolean(rate.id) && !rate.isDeleted)?.id ??
            undefined;
          // Copy-and-edit (TRE-15848 bugfix): a copied project strips every SRS id (all ops become
          // creates) while `projectId` still points to the SOURCE project it was copied from (used
          // only for data-fetching the source rates). The creates land on a NEW, not-yet-persisted
          // project with no cascade audience, so any preview keyed off `projectId` would disclose the
          // SOURCE project's orders — a wrong, misleading impact list. A brand-new project has no
          // existing orders to cascade to, so skip the preview entirely and save silently (same regime
          // as the documented "no projectId / no persisted card" gap). Only gate here, NOT inside
          // `hasCascadingChange`, which the order-origin block below reuses.
          if (
            scope === 'project' &&
            !isCopyAndEdit &&
            applyRateChangesToExistingOrders &&
            companyId &&
            hasCascadingChange
          ) {
            // Initial fetch uses the default range (From = today, To = open): "apply from
            // today forward". The dialog can then refetch as the user narrows the range.
            // Both preview fetches fail CLOSED: on error, abort the save + tell the user
            // rather than cascading to an undisclosed set of orders.
            let inRange: ImpactedOrderRow[] = [];
            let anyReachable = false;
            try {
              // App default timezone, matching the drawer's default From (its DateInput
              // renders in that timezone — a system-tz "today" disagrees near midnight).
              const initialRange = { from: dayjs.tz().format('YYYY-MM-DD') };
              inRange = await fetchImpactedOrders(
                operations,
                initialRange,
                undefined,
                representativeCardId,
              );
              // Open the dialog when the default range has orders OR there are ANY reachable
              // orders at all (all past-dated) that the user could widen the range to include.
              // Only a truly zero-impact save proceeds silently. The second fetch is
              // short-circuited to just the empty-default-range case.
              anyReachable =
                inRange.length > 0 ||
                (
                  await fetchImpactedOrders(
                    operations,
                    undefined,
                    undefined,
                    representativeCardId,
                    true, // existence-only: this widen probe just needs "any reachable?"
                  )
                ).length > 0;
            } catch {
              // Fail closed: if we can't determine which existing orders this cascading
              // change would reach, do NOT apply it blind. Abort the save and tell the user.
              toasterStore.push(
                alert(
                  t('project.form.rate_change_impact_preview_failed'),
                  AlertTypes.error,
                ),
              );
              return false;
            }
            if (anyReachable) {
              // Let the dialog re-query the BE for a new range against this same operations set.
              impactFetchRef.current = (from, to, signal) =>
                fetchImpactedOrders(
                  operations,
                  { from, to, signal },
                  undefined,
                  representativeCardId,
                );
              const result = await new Promise<RateChangeImpactConfirmResult | 'cancel'>(
                (resolve) => {
                  impactResolveRef.current = resolve;
                  // Seed with the today-forward view so the picker (default today) matches the
                  // list; the user can clear From to widen to the past-dated orders.
                  setImpactDialogOrders(inRange);
                },
              );
              if (result === 'cancel') return false;
              // Reconcile the referenced set (complete in-range referenced list) via the
              // dedicated endpoint BEFORE the card writes. On failure abort the whole save.
              const rateReferenceOutcome = await runRateReferenceForConfirm(result);
              if (rateReferenceOutcome === 'abort') return false;
              operations.rateReferenceApplied = rateReferenceOutcome === 'applied';
              operations.skippedOrderIds = result.skippedOrderIds;
              operations.from = result.from;
              operations.to = result.to;
            }
          }

          // Selective rate apply at the ORDER level. An order whose rate ops write the project's
          // cards (target='project') can reach other orders, so those saves get the origin stamp and
          // the impact dialog. The order form decides that with `orderRateWriteIsProjectScoped`; an
          // order that owns its own cards — a decoupling stamp or the durable-decouple clones — is
          // excluded there and edits stay order-local, so no dialog. The `ratesSyncStatus` guard is
          // belt-and-braces for the same exclusion.
          //
          // `originOrderId` is OPTIONAL here: on create and copy & edit the order does not exist yet,
          // so there is no origin to name at this point. `OrderForm` stamps the saved order's id onto
          // the operations after the POST — before that the cascade would spare nothing, because the
          // backend computes its skip set as "everything reachable minus the origin".
          //
          // Deliberately NOT gated on `hasCascadingChange`: the checkbox stamped below is a
          // direct control, and an unchecked box decouples the order on save even when the save
          // carries no rate edit at all. Only the sidesheet needs a cascading change to preview.
          const isOrderOriginProjectScoped =
            scope === 'order' &&
            Boolean(orderRateWriteIsProjectScoped) &&
            ratesSyncStatus !== 'stamped' &&
            Boolean(companyId);
          if (isOrderOriginProjectScoped) {
            // The origin order ALWAYS receives its own edit, so stamp it whenever it is known — on BOTH
            // toggle states, and OUTSIDE the dialog gate so an origin-only order (no siblings → dialog
            // skipped) still carries it. The origin is also filtered out of the dialog list so it can
            // never be unchecked. Both backend branches subtract the origin from the skip set.
            if (originOrderId) {
              operations.originOrderId = originOrderId;
            }
            // Stamp the checkbox on BOTH states. `true` travels to the project-scoped write as the
            // per-request cascade override; honouring it needs `edit_project`, which is why the order
            // form forces it on and hides the box for actors who lack it rather than offering a choice
            // the backend discards. `false` never reaches the wire — the order form reads it back off
            // these operations and routes the save onto the order's own copies instead.
            operations.applyRateChangesToExistingOrders = Boolean(
              applyRateChangesToExistingOrders,
            );

            // The permission gates the sidesheet independently of the toggle, which the order form
            // forces ON for an actor who lacks it (see the prop docs).
            if (
              hasCascadingChange &&
              applyRateChangesToExistingOrders &&
              canEditProject === true
            ) {
              // Toggle ON: same fail-closed preview + dialog flow as the project path, but the origin
              // order is filtered out of EVERY fetch so it never appears as a skippable sibling row.
              let inRange: ImpactedOrderRow[] = [];
              let anyReachable = false;
              try {
                // App default timezone, matching the drawer's default From (its DateInput
                // renders in that timezone — a system-tz "today" disagrees near midnight).
                const initialRange = { from: dayjs.tz().format('YYYY-MM-DD') };
                inRange = await fetchImpactedOrders(
                  operations,
                  initialRange,
                  originOrderId,
                  representativeCardId,
                );
                anyReachable =
                  inRange.length > 0 ||
                  (
                    await fetchImpactedOrders(
                      operations,
                      undefined,
                      originOrderId,
                      representativeCardId,
                      true, // existence-only: this widen probe just needs "any reachable?"
                    )
                  ).length > 0;
              } catch {
                toasterStore.push(
                  alert(
                    t('project.form.rate_change_impact_preview_failed'),
                    AlertTypes.error,
                  ),
                );
                return false;
              }
              if (anyReachable) {
                impactFetchRef.current = (from, to, signal) =>
                  fetchImpactedOrders(
                    operations,
                    { from, to, signal },
                    originOrderId,
                    representativeCardId,
                  );
                const result = await new Promise<
                  RateChangeImpactConfirmResult | 'cancel'
                >((resolve) => {
                  impactResolveRef.current = resolve;
                  setImpactDialogOrders(inRange);
                });
                if (result === 'cancel') return false;
                // Reconcile the referenced set via the dedicated endpoint BEFORE the card
                // writes (order-origin cascade routes to the project card). `originOrderId` is sent as
                // `origin_order_id` so the BE keeps the origin referenced (it is excluded from the
                // dialog list, so it is never in `referencedOrderIds` on its own). Abort on failure.
                const rateReferenceOutcome = await runRateReferenceForConfirm(
                  result,
                  originOrderId,
                );
                if (rateReferenceOutcome === 'abort') return false;
                operations.rateReferenceApplied = rateReferenceOutcome === 'applied';
                operations.skippedOrderIds = result.skippedOrderIds;
                operations.from = result.from;
                operations.to = result.to;
              }
            }
          }

          callback({
            customerRate: customerRate.data,
            vendorRate: vendorRate.data,
            driverRate: driverRate.data,
            customerRateAddOnAssignments: customerRate.addOnAssignments,
            vendorRateAddOnAssignments: vendorRate.addOnAssignments,
            driverRateAddOnAssignments: driverRate.addOnAssignments,
            siteRateScheduleOperations: operations,
          });
          return true;
        },
      }),
      [
        showDriverRate,
        siteRates,
        makeOperationsList,
        clearErrorForClientKey,
        isCopyAndEdit,
        isEditingOrder,
        useVendorRateForDriver,
        scope,
        applyRateChangesToExistingOrders,
        companyId,
        fetchImpactedOrders,
        runRateReferenceForConfirm,
        toasterStore,
        ratesSyncStatus,
        orderRateWriteIsProjectScoped,
        originOrderId,
        canEditProject,
        isFreightRateRequired,
        isAwaitingDefaultVendorRateToDriverFF,
      ],
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <FreightRatesManager
          siteRates={siteRates}
          setSiteRates={setSiteRates}
          isLoading={isLoading}
          isCopyAndEdit={isCopyAndEdit}
          accountId={accountId}
          projectId={projectId}
          onDirtyStatusChange={onDirtyStatusChange}
          prevailingWage={prevailingWage}
          validateRef={validateFreightRatesRef}
          defaultSiteRateScheduleId={defaultSiteRateScheduleId}
          onDefaultSiteRateScheduleChange={onDefaultSiteRateScheduleChange}
          errorsByClientKey={errorsByClientKey}
          resetGeneration={resetGeneration}
          onClearError={clearErrorForClientKey}
          scope={scope}
          companyId={companyId}
          orderDate={orderDate}
          applyRateChangesToExistingOrders={applyRateChangesToExistingOrders}
          isFreightRateRequired={isFreightRateRequired}
        />
        {bottomContent}
        {impactDialogOrders !== null && (
          <RateChangeImpactDrawer
            isOpen
            orders={impactDialogOrders}
            showDateRange
            // FreightRates always uses the single referenced-list model — confirm reports
            // the complete in-range referenced set and guards the full-dereference case.
            isReferencedModel
            // An order-origin edit forces its own order into the referenced set via `origin_order_id`
            // (sent by `runRateReferenceForConfirm`), so an empty in-range checked set is NOT a full
            // dereference there — suppress the confirm. Undefined origin (project scope) → false.
            hasOriginReference={Boolean(originOrderId)}
            scope={scope}
            fetchOrders={(from, to, signal) =>
              impactFetchRef.current?.(from, to, signal) ?? Promise.resolve([])
            }
            onConfirm={(result) => {
              impactResolveRef.current?.(result);
              impactResolveRef.current = null;
              impactFetchRef.current = null;
              setImpactDialogOrders(null);
            }}
            onClose={() => {
              impactResolveRef.current?.('cancel');
              impactResolveRef.current = null;
              impactFetchRef.current = null;
              setImpactDialogOrders(null);
            }}
          />
        )}
      </Box>
    );
  },
);

export default FreightRates;
